import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
} from 'firebase/auth';
import type { User, UserCredential } from 'firebase/auth';
import type { FirebaseAuthError } from '@/types';
import { auth } from './config';

/**
 * Authentication service for Digital Menu SaaS
 * Provides methods for user registration, login, logout, and password reset
 */

export interface SignUpData {
  email: string;
  password: string;
  displayName: string;
}

export interface SignInData {
  email: string;
  password: string;
}

/**
 * Create a new user account with email and password
 */
export const signUp = async ({ email, password, displayName }: SignUpData): Promise<UserCredential> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update the user's display name
    await updateProfile(userCredential.user, {
      displayName,
    });

    return userCredential;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Sign in an existing user with email and password
 */
export const signIn = async ({ email, password }: SignInData): Promise<UserCredential> => {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error: unknown) {
    console.error('Error signing in:', error);
    
    // Provide generic error messages to prevent email enumeration
    const firebaseError = error as FirebaseAuthError;
    if (firebaseError.code === 'auth/user-not-found' || 
        firebaseError.code === 'auth/wrong-password' || 
        firebaseError.code === 'auth/invalid-credential') {
      // Create a proper FirebaseAuthError-like object
      const customError: FirebaseAuthError = {
        code: 'auth/invalid-credential',
        message: 'Invalid email or password'
      };
      throw customError;
    }
    
    // For all other errors, throw as-is
    throw error;
  }
};

/**
 * Sign out the current user
 */
export const logOut = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Send a password reset email to the user
 * Note: Firebase intentionally doesn't reveal if email exists for security
 */
export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: unknown) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};

/**
 * Get the current authenticated user
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

/**
 * Check if a user is currently authenticated
 */
export const isAuthenticated = (): boolean => {
  return auth.currentUser !== null;
};

// Export auth instance for direct use if needed
export { auth };
